import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { fmtCurrency, fmtDate } from './helpers';

const LedgerTab = ({
  ledgerLoading, ledger,
  ledgerFilter, setLedgerFilter,
  showEmptyParties, setShowEmptyParties,
  expandedLedgerGroups, setExpandedLedgerGroups,
}) => {
  return (
    <>
      {ledgerLoading && <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>}
      {ledger && !ledgerLoading && (
        <>
          {/* Ledger Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Earned</p><p className="text-lg font-bold text-indigo-700">{fmtCurrency(ledger.summary.total_earned)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Deducted</p><p className="text-lg font-bold text-red-600">{fmtCurrency(ledger.summary.total_deducted)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Net Commission</p><p className="text-lg font-bold text-emerald-700">{fmtCurrency(ledger.summary.net_commission)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Paid</p><p className="text-lg font-bold text-green-700">{fmtCurrency(ledger.summary.total_paid)}</p></CardContent></Card>
            <Card className="border-2 border-orange-300 bg-orange-50/50"><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 font-medium">Balance Due</p><p className="text-lg font-bold text-orange-800">{fmtCurrency(ledger.summary.balance_due)}</p></CardContent></Card>
          </div>

          {/* Filter bar */}
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <div className="flex gap-1">
                <Button size="sm" variant={ledgerFilter === 'all' ? 'default' : 'outline'}
                  className={ledgerFilter === 'all' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                  onClick={() => setLedgerFilter('all')} data-testid="ledger-filter-all">All</Button>
                <Button size="sm" variant={ledgerFilter === 'paid' ? 'default' : 'outline'}
                  className={ledgerFilter === 'paid' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setLedgerFilter('paid')} data-testid="ledger-filter-paid">Paid only</Button>
                <Button size="sm" variant={ledgerFilter === 'unpaid' ? 'default' : 'outline'}
                  className={ledgerFilter === 'unpaid' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  onClick={() => setLedgerFilter('unpaid')} data-testid="ledger-filter-unpaid">Unpaid only</Button>
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-600 ml-2 cursor-pointer">
                <input type="checkbox" checked={showEmptyParties} onChange={e => setShowEmptyParties(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  data-testid="show-empty-parties-toggle" />
                Show parties with no matching entries
              </label>
            </CardContent>
          </Card>

          {/* Ledger Table — grouped by party */}
          {(() => {
            const groups = {};
            ledger.entries.forEach(e => {
              const key = e.customer || 'Unknown';
              if (!groups[key]) groups[key] = [];
              groups[key].push(e);
            });
            const filteredGroups = {};
            Object.keys(groups).forEach(party => {
              const all = groups[party].slice().sort((a, b) => (a.date || '').localeCompare(b.date || ''));
              const filtered = ledgerFilter === 'all' ? all
                : ledgerFilter === 'paid' ? all.filter(e => e.payment_status === 'paid')
                : all.filter(e => e.payment_status !== 'paid');
              if (filtered.length > 0 || showEmptyParties) {
                filteredGroups[party] = filtered;
              }
            });
            const sortedPartyNames = Object.keys(filteredGroups).sort((a, b) => a.localeCompare(b));
            if (sortedPartyNames.length === 0) {
              return <Card><CardContent className="p-8 text-center text-gray-400">No entries match the selected filter</CardContent></Card>;
            }
            return (
              <div className="space-y-3" data-testid="ledger-groups">
                {sortedPartyNames.map(party => {
                  const entries = filteredGroups[party];
                  const allEntries = groups[party];
                  const earned = allEntries.reduce((s, e) => s + (e.earned || 0), 0);
                  const deducted = allEntries.reduce((s, e) => s + (e.deducted || 0), 0);
                  const net = earned - deducted;
                  const paid = allEntries.filter(e => e.payment_status === 'paid')
                    .reduce((s, e) => s + (e.earned || 0) - (e.deducted || 0), 0);
                  const balance = net - paid;
                  const isExpanded = expandedLedgerGroups[party] !== false;

                  let runningBalance = 0;
                  const entriesWithBalance = entries.map(e => {
                    runningBalance += (e.earned || 0) - (e.deducted || 0);
                    return { ...e, party_running: runningBalance };
                  });

                  return (
                    <Card key={party} data-testid={`ledger-party-${party}`}>
                      <CardContent className="p-0">
                        <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 flex items-center justify-between cursor-pointer hover:bg-indigo-100/50"
                          onClick={() => setExpandedLedgerGroups(prev => ({ ...prev, [party]: !isExpanded }))}>
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-indigo-600" /> : <ChevronDown className="w-4 h-4 text-indigo-600" />}
                            <span className="font-bold text-gray-800">{party}</span>
                            <Badge className="text-[10px] bg-gray-200 text-gray-600">{entries.length} {ledgerFilter === 'all' ? 'entries' : ledgerFilter}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span><span className="text-gray-500">Earned:</span> <span className="font-bold text-green-700">{fmtCurrency(earned)}</span></span>
                            <span><span className="text-gray-500">Deducted:</span> <span className="font-bold text-red-600">{fmtCurrency(deducted)}</span></span>
                            <span><span className="text-gray-500">Paid:</span> <span className="font-bold text-emerald-700">{fmtCurrency(paid)}</span></span>
                            <span className="px-2 py-1 rounded-md bg-orange-100 text-orange-800 font-bold">Bal Due: {fmtCurrency(balance)}</span>
                          </div>
                        </div>
                        {isExpanded && entries.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead><tr className="bg-gray-50 border-b border-gray-200">
                                <th className="px-3 py-2 text-left text-xs font-medium">Date</th>
                                <th className="px-3 py-2 text-center text-xs font-medium w-[50px]">Type</th>
                                <th className="px-3 py-2 text-left text-xs font-medium">Inv/CN #</th>
                                <th className="px-3 py-2 text-right text-xs font-medium">Amount</th>
                                <th className="px-3 py-2 text-center text-xs font-medium">Comm %</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-green-700">Earned</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-red-600">Deducted</th>
                                <th className="px-3 py-2 text-right text-xs font-medium">Running</th>
                                <th className="px-3 py-2 text-center text-xs font-medium">Status</th>
                                <th className="px-3 py-2 text-left text-xs font-medium">Paid Date</th>
                              </tr></thead>
                              <tbody>
                                {entriesWithBalance.map((e, i) => (
                                  <tr key={i} className={`border-b border-gray-100 ${e.type === 'SR' ? 'bg-red-50/30' : ''} ${e.payment_status === 'paid' ? 'bg-green-50/30' : ''}`}>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(e.date)}</td>
                                    <td className="px-3 py-2 text-center"><Badge className={`text-[9px] ${e.type === 'SE' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{e.type}</Badge></td>
                                    <td className="px-3 py-2 font-mono text-xs" title={e.ref}>
                                      {e.invoice ? e.invoice : <span className="text-gray-400 italic">{e.ref || '-'}</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs">{fmtCurrency(e.amount)}</td>
                                    <td className="px-3 py-2 text-center text-xs">{e.commission_pct}%</td>
                                    <td className="px-3 py-2 text-right text-xs font-medium text-green-700">{e.earned > 0 ? fmtCurrency(e.earned) : '-'}</td>
                                    <td className="px-3 py-2 text-right text-xs font-medium text-red-600">{e.deducted > 0 ? `-${fmtCurrency(e.deducted)}` : '-'}</td>
                                    <td className="px-3 py-2 text-right text-xs font-bold">{fmtCurrency(e.party_running)}</td>
                                    <td className="px-3 py-2 text-center">
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${e.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                        {e.payment_status}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{e.payment_status === 'paid' ? fmtDate(e.payment_date) : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {isExpanded && entries.length === 0 && (
                          <div className="px-4 py-3 text-xs text-gray-400 italic">No entries match the current filter for this party.</div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}
    </>
  );
};

export default LedgerTab;
